import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import {
  type Suggestion,
  type SuggestionStatus,
  listAllSuggestions,
  listSuggestionVotes,
  fetchEligibleVotersCount,
  createSuggestion,
  updateSuggestion,
  reviewSuggestion,
  registerSuggestionVote,
  removeSuggestionVote,
  deleteSuggestion,
} from '../services/suggestionsService'

export type SuggestionWithStats = Suggestion & {
  votesUp: number
  votesDown: number
  eligibleTotal: number
  targetMin: number
  progress: number
  currentUserVote: 'up' | 'down' | null
}

export function useSuggestions() {
  const { user, profile } = useAuth()
  const userId = user?.id ?? null

  const [items, setItems] = React.useState<SuggestionWithStats[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [eligibleTotal, setEligibleTotal] = React.useState<number | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [suggestions, votes, eligible] = await Promise.all([
        listAllSuggestions(),
        listSuggestionVotes(),
        fetchEligibleVotersCount(),
      ])
      const eligibleCount = eligible > 0 ? eligible : 0
      setEligibleTotal(eligibleCount)

      const bySuggestion = new Map<
        string,
        { up: number; down: number; myVote: 'up' | 'down' | null }
      >()
      for (const v of votes) {
        const entry = bySuggestion.get(v.suggestion_id) ?? {
          up: 0,
          down: 0,
          myVote: null,
        }
        if (v.vote === 'up') entry.up += 1
        else entry.down += 1
        if (userId && v.user_id === userId) entry.myVote = v.vote
        bySuggestion.set(v.suggestion_id, entry)
      }

      const enriched: SuggestionWithStats[] = suggestions.map((s) => {
        const agg = bySuggestion.get(s.id) ?? {
          up: 0,
          down: 0,
          myVote: null,
        }
        const thresholdRaw = s.approval_threshold_pct ?? 51
        const thresholdPct =
          typeof thresholdRaw === 'number' && Number.isFinite(thresholdRaw)
            ? Math.min(100, Math.max(1, Math.round(thresholdRaw)))
            : 51
        const targetMin =
          eligibleCount > 0
            ? Math.ceil((eligibleCount * thresholdPct) / 100)
            : 0
        const progress =
          targetMin > 0 ? Math.min(100, Math.round((agg.up * 100) / targetMin)) : 0
        return {
          ...s,
          votesUp: agg.up,
          votesDown: agg.down,
          eligibleTotal: eligibleCount,
          targetMin,
          progress,
          currentUserVote: agg.myVote,
        }
      })
      setItems(enriched)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar sugestões')
    } finally {
      setLoading(false)
    }
  }, [userId])

  React.useEffect(() => {
    load()
  }, [load])

  async function handleCreate(title: string, content: string) {
    if (!user) throw new Error('Usuário não autenticado')
    const authorName = profile?.full_name || user.email || 'Usuário'
    const authorAvatarUrl = profile?.avatar_url ?? null
    await createSuggestion({ title, content, authorName, authorAvatarUrl })
    await load()
  }

  async function handleUpdate(
    id: string,
    patch: { title?: string; content?: string; thresholdPct?: number },
  ) {
    await updateSuggestion(id, {
      title: patch.title,
      content: patch.content,
      approvalThresholdPct: (patch as any).thresholdPct,
    })
    await load()
  }

  async function handleReview(
    id: string,
    action: 'approve' | 'reject',
    opts: {
      note?: string | null
      visibility?: 'author' | 'public'
      authorPrivateNote?: string | null
    },
  ) {
    await reviewSuggestion(id, action, opts)
    await load()
  }

  async function handleVote(id: string, action: 'up' | 'down' | 'clear') {
    const current =
      items.find((it) => it.id === id)?.currentUserVote ?? null

    const isClearAction = action === 'clear' || action === current

    const res = isClearAction
      ? await removeSuggestionVote(id)
      : await registerSuggestionVote(id, action as 'up' | 'down')

    const baseEligible =
      res.eligible_total > 0
        ? res.eligible_total
        : eligibleTotal && eligibleTotal > 0
        ? eligibleTotal
        : 0

    const currentSuggestion = items.find((it) => it.id === id) ?? null
    const thresholdRaw = currentSuggestion?.approval_threshold_pct ?? 51
    const thresholdPct =
      typeof thresholdRaw === 'number' && Number.isFinite(thresholdRaw)
        ? Math.min(100, Math.max(1, Math.round(thresholdRaw)))
        : 51

    const target =
      baseEligible > 0 ? Math.ceil((baseEligible * thresholdPct) / 100) : 0
    const progress =
      target > 0 ? Math.min(100, Math.round((res.votes_up * 100) / target)) : 0

    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? ({
              ...it,
              status: res.status as SuggestionStatus,
              votesUp: res.votes_up,
              votesDown: res.votes_down,
              eligibleTotal: res.eligible_total,
              targetMin: target,
              progress,
              currentUserVote: res.user_vote,
            } as SuggestionWithStats)
          : it,
      ),
    )
  }

  async function handleRemove(id: string) {
    await deleteSuggestion(id)
    setItems((prev) => prev.filter((it) => it.id === id ? false : true))
  }

  return {
    items,
    loading,
    error,
    reload: load,
    create: handleCreate,
    update: handleUpdate,
    review: handleReview,
    vote: handleVote,
    remove: handleRemove,
    role: profile?.role ?? null,
    userId,
  }
}
